import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Theme,
  useTheme,
  Select,
  FormControl,
  MenuItem,
  Tooltip,
  Button,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import customworkspaceStyles from './ccf-custom-workspace.styles';
import {
  CcfHeader,
  CcfReturnTabIcon,
  CcfTypography,
  CcfViewInNewWindowIcon,
  CcfOpenNewTabIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import {
  getActivePersistentPanel,
  getCustomWorkspaces,
  globalActions,
} from '../global.app.slice';
import {
  LocalStorageHelper,
  PersistentPanel,
  StorageKeys,
} from '@nice-ccf/core-sdk';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { awsS3Config } from '../../util/common';
import AWS from 'aws-sdk';

/**
 * S3 Workspace Data Interface
 */
interface S3WorkspaceData {
  customWorkspaceLabel1?: string;
  customWorkspaceLabel2?: string;
  customWorkspaceLabel3?: string;
  customWorkspaceUrl1?: string;
  customWorkspaceUrl2?: string;
  customWorkspaceUrl3?: string;
}

/**
 * Custom workspace props
 */
interface CcfCustomWorkspaceProps {
  /**
   * @remarks - Currently active custom workspace
   */
  activeCustomSpaceIframe?: PersistentPanelFrame;
}

/**
 * Declared individual objects containing label and iframe element
 */
export interface PersistentPanelFrame {
  /**
   * @remarks - label of custom workspace
   */
  label: string;
  /**
   * @remarks - Custom workspace iframe element
   */
  frame: () => JSX.Element;
}

/**
 * Custom Workpsace Container which includes header and iframe
 * @example - <CcfcustomWorkspace />
 * @returns JSX.Element
 */
const CcfCustomWorkspace = (props: CcfCustomWorkspaceProps) => {
  const dispatch = useDispatch();
  const theme: Theme = useTheme();
  const styles = customworkspaceStyles(theme);
  const [translate] = useTranslator();
  
  // Redux selectors
  const customWorkspacesInitialData = useSelector(getCustomWorkspaces);
  const initialActiveCustomWorkspace = useSelector(getActivePersistentPanel);
  
  // Component state
  const [customWorkspaces, setCustomWorkspaces] = useState<Array<PersistentPanel>>([]);
  const [activeCustomWorkspace, setActiveCustomWorkspace] = useState<PersistentPanel>({
    ...initialActiveCustomWorkspace,
  });
  const [customWorkspaceLabel, setCustomWorkspaceLabel] = useState<string>(
    initialActiveCustomWorkspace?.persistentPanelLabel || ''
  );
  const [isWindowPoppedout, setWindowPopoutStatus] = useState(false);
  const [cwWindow, setCwWindow] = useState<Window | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Use ref to track mounted state to prevent memory leaks
  const componentMountedRef = useRef(true);

  // User credentials for S3 path
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const credentials = localStorage.getItem('credentials');
  const parsedCredentials = credentials ? JSON.parse(credentials) : null;
  const loginCredentialsFromSSO = accounts[0]?.username || ' ';
  const agentName = isAuthenticated
    ? loginCredentialsFromSSO
    : parsedCredentials?.username;

  // S3 configuration
  const s3Config = awsS3Config;
  const S3_BUCKET = s3Config.s3Bucket;
  const initialObjectKey = `simulatorDB/${agentName}/custom-workspace/custom-workspace.json`;

  // Initialize S3 client once
  const s3 = useMemo(() => {
    AWS.config.update({
      accessKeyId: s3Config.accessKeyId,
      secretAccessKey: s3Config.secretAccessKey,
      region: s3Config.region,
    });
    return new AWS.S3();
  }, [s3Config]);

  /**
   * Fetches workspace data from S3
   * @returns The workspace data or null if error
   */
  const fetchS3Object = useCallback(async (): Promise<S3WorkspaceData | null> => {
    const params = {
      Bucket: S3_BUCKET,
      Key: initialObjectKey,
    };

    try {
      // Fetch data from S3
      const objectData = await s3.getObject(params).promise();
      const objectDataJson = JSON.parse(
        objectData.Body?.toString('utf-8') ?? ''
      );

      console.log('Fetched object data from S3:', objectDataJson);
      return objectDataJson;
    } catch (error) {
      console.error('Error fetching object from S3:', error);
      return null;
    }
  }, [S3_BUCKET, initialObjectKey, s3]);

  /**
   * Set the active workspace
   * @param workspace - The workspace to set as active
   */
  const handleSetActiveWorkspace = useCallback((workspace: PersistentPanel) => {
    // Update localStorage
    LocalStorageHelper.setItem(StorageKeys.ACTIVE_CUSTOMWORKSPACE, workspace);
    
    // Update component state
    setActiveCustomWorkspace(workspace);
    setCustomWorkspaceLabel(workspace.persistentPanelLabel);
    
    // Close any popped out window
    setWindowPopoutStatus(false);
    
    // Update Redux state
    dispatch(globalActions.setActivePersistentPanel(workspace));
  }, [dispatch]);

  /**
   * Initialize workspaces from data
   * @param data - The S3 workspace data
   * @returns Array<PersistentPanel> - The initialized workspaces
   */
  const initializeWorkspaces = useCallback((data: S3WorkspaceData | null): Array<PersistentPanel> => {
    // Use S3 data with fallback to initial data
    return [
      {
        persistentPanelId: 1,
        persistentPanelLabel: data?.customWorkspaceLabel1 || customWorkspacesInitialData[0]?.persistentPanelLabel,
        persistentPanelURI: data?.customWorkspaceUrl1 || customWorkspacesInitialData[0]?.persistentPanelURI,
      },
      {
        persistentPanelId: 2,
        persistentPanelLabel: data?.customWorkspaceLabel2 || customWorkspacesInitialData[1]?.persistentPanelLabel,
        persistentPanelURI: data?.customWorkspaceUrl2 || customWorkspacesInitialData[1]?.persistentPanelURI,
      },
      {
        persistentPanelId: 3,
        persistentPanelLabel: data?.customWorkspaceLabel3 || customWorkspacesInitialData[2]?.persistentPanelLabel,
        persistentPanelURI: data?.customWorkspaceUrl3 || customWorkspacesInitialData[2]?.persistentPanelURI,
      },
    ];
  }, [customWorkspacesInitialData]);



  /**
   * Main data fetching function
   * Shows loading state, fetches data, initializes workspaces
   */
  const loadWorkspaceData = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const data = await fetchS3Object();
      
      const workspaces = initializeWorkspaces(data);
      
      // Update the workspaces list state
      setCustomWorkspaces(workspaces);
      
      // Always set an active workspace with the fresh S3 data
      if (workspaces.length > 0) {
        // Try to find the currently selected workspace in the new data
        const currentActiveId = activeCustomWorkspace?.persistentPanelId;
        const updatedActiveWorkspace = currentActiveId 
          ? workspaces.find(ws => ws.persistentPanelId === currentActiveId) || workspaces[0]
          : workspaces[0];
        
        console.log('Setting active workspace to:', updatedActiveWorkspace);
        
        // Update all related states with the fresh S3 data
        setActiveCustomWorkspace(updatedActiveWorkspace);
        setCustomWorkspaceLabel(updatedActiveWorkspace.persistentPanelLabel);
        LocalStorageHelper.setItem(StorageKeys.ACTIVE_CUSTOMWORKSPACE, updatedActiveWorkspace);
        dispatch(globalActions.setActivePersistentPanel(updatedActiveWorkspace));
        dispatch(globalActions.setPersistentPanels(workspaces));
      }
    } catch (error) {
      console.error('Error loading workspace data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchS3Object, initializeWorkspaces, activeCustomWorkspace?.persistentPanelId, dispatch]);

  /**
   * Open the workspace URL in a new tab
   */
  const openInNewTab = useCallback(() => {
    if (!activeCustomWorkspace?.persistentPanelURI) return;
    
    setWindowPopoutStatus(true);
    const openedWindow = window.open(
      activeCustomWorkspace.persistentPanelURI,
      activeCustomWorkspace.persistentPanelLabel
    );
    
    if (openedWindow) {
      openedWindow.focus();
      setCwWindow(openedWindow);
    }
  }, [activeCustomWorkspace]);

  /**
   * Close the popped out window and return to iframe view
   */
  const returnToAgentView = useCallback(() => {
    setWindowPopoutStatus(false);
    if (cwWindow) {
      cwWindow.close();
      setCwWindow(null);
    }
  }, [cwWindow]);

  // Load data on component mount
  useEffect(() => {
    componentMountedRef.current = true;
    loadWorkspaceData();
    
    return () => {
      componentMountedRef.current = false;
      localStorage.removeItem('customWorkspacePanel');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Listen for changes in Redux active workspace (e.g., from popover selection)
  useEffect(() => {
    if (initialActiveCustomWorkspace?.persistentPanelId) {
      // Check if the Redux state is different from our local state
      if (initialActiveCustomWorkspace.persistentPanelId !== activeCustomWorkspace?.persistentPanelId) {        
        // Update local state to match Redux state
        setActiveCustomWorkspace(initialActiveCustomWorkspace);
        setCustomWorkspaceLabel(initialActiveCustomWorkspace.persistentPanelLabel);
      }
    }
  }, [initialActiveCustomWorkspace, activeCustomWorkspace?.persistentPanelId]);

  /**
   * Render component
   */
  return (
    <Box data-testid="customWorkspace-container" sx={styles.iframeContainer}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'transparent',
          position: 'absolute',
        }}
        open={isLoading}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={50} color="primary" />
          <CcfTypography sx={{ color: 'primary.main' }}>
            Loading Custom Workspace...
          </CcfTypography>
        </Box>
      </Backdrop>
      
      {/* Header Section */}
      <Box component="section" sx={styles.customWorkspaceHeader}>
        <CcfHeader RightIcon={false} />
        <Grid container direction={'row'} justifyContent={'space-between'}>
          {/* Workspace Dropdown */}
          <Grid>
            <FormControl
              variant="standard"
              sx={styles.customDropdown}
              size="small"
            >
              <Select
                labelId="workspace-select-label"
                id="workspace-select"
                data-testid="customworkspace-menu"
                value={customWorkspaceLabel || ''}
                label={translate('customWorkspace')}
                disableUnderline
                disabled={isLoading}
              >
                {customWorkspaces && customWorkspaces.length > 0 ? (
                  customWorkspaces.map((item, index) => (
                    <MenuItem
                      key={item.persistentPanelId}
                      value={item.persistentPanelLabel}
                      data-testid={`customworkspace-menu-item-${index}`}
                      onClick={() => handleSetActiveWorkspace(item)}
                    >
                      {item.persistentPanelLabel}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>{translate('noWorkspacesAvailable')}</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Open/Return Button */}
          <Grid
            data-testid="pop-out-pop-in-buttons"
            sx={styles.openinnewtabIcon}
          >
            {isWindowPoppedout ? (
              <Tooltip title={translate('returnToAgentView')} arrow>
                <Button sx={styles.openinnewTabButton}>
                  <CcfReturnTabIcon
                    data-testid="return-to-agent-view"
                    onClick={returnToAgentView}
                  />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={translate('openInNewTab')} arrow>
                <Button sx={styles.openinnewTabButton}>
                  <CcfOpenNewTabIcon
                    data-testid="open-in-new-tab"
                    onClick={openInNewTab}
                  />
                </Button>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Box>
      
      {/* Content Section - Either popup message or iframe */}
      {isWindowPoppedout ? (
        <Box
          data-testid="open-in-new-tab-window"
          sx={styles.openInNewWindowContainer}
        >
          <Box sx={styles.openInNewWindowIcon}>
            <CcfViewInNewWindowIcon />
          </Box>
          <Box sx={styles.openInNewWindowContent}>
            <Box>
              <CcfTypography className="messageText">
                {translate('viewingInBrowserMessage')}
              </CcfTypography>
              <Box className="link_text">
                <CcfTypography
                  data-testid="click-to-open-in-new-tab"
                  className="link"
                  variant="h5"
                  tabIndex={0}
                  onClick={openInNewTab}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') openInNewTab();
                  }}
                >
                  {translate('clickHere')}
                </CcfTypography>
                <CcfTypography variant="h5">
                  {translate('viewInBrowser')}
                </CcfTypography>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <iframe
          title={translate('customWorkspace')}
          security="none"
          referrerPolicy="unsafe-url"
          src={activeCustomWorkspace?.persistentPanelURI}
          width={'100%'}
          className="customworkspace-iframe"
        />
      )}
    </Box>
  );
};

export default CcfCustomWorkspace;
